<template>
  <div
    class="stats-renderer"
    :class="{ 'stats-renderer--in-explorer': inExplorer }"
    @click="inExplorer && $emit('open-drilldown')"
  >
    <AnalyticsStats
      :stats="stats"
      :icons="icons"
      :colors="colors"
      :format="measureDefaultFormat"
      :with-comparison="withComparison"
      :is-centered="inExplorer"
      :is-favorable="measureFavorability"
    />
  </div>
</template>

<script lang="ts">
import { type Stat } from '~/types/analytics'
import type { QueryResults, ResultSetItem } from '~/types/query'

export default {
  name: 'RendererStats',
  props: {
    queryResults: {
      type: Object as PropType<QueryResults>,
      required: true
    },
    icons: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    colors: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    withComparison: {
      type: Boolean,
      default: true
    },
    inExplorer: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'open-drilldown': () => true
  },
  computed: {
    measureFavorability() {
      return this.queryResults.query.measures[0].meta.isFavorable
    },
    measureDefaultFormat() {
      return this.queryResults.query.measures[0].meta.format
    },
    stats(): Stat[] {
      const currentPeriodValues = this.queryResults.data[0]

      if (!currentPeriodValues) {
        return [
          {
            value: null,
            evolution: null
          }
        ]
      }

      const lastPeriodValues = this.queryResults.data[1]

      return this.queryResults.query.measures.map(measure => ({
        value: currentPeriodValues[measure.name] as number | null,
        ...(this.withComparison &&
          lastPeriodValues && {
            evolution: this.computeEvolution(
              lastPeriodValues,
              currentPeriodValues,
              measure.name
            )
          })
      }))
    }
  },
  methods: {
    computeEvolution(
      lastPeriodValues: ResultSetItem,
      currentPeriodValues: ResultSetItem,
      measureName: string
    ) {
      if (
        !Number.isFinite(lastPeriodValues[measureName]) ||
        !Number.isFinite(currentPeriodValues[measureName])
      ) {
        return null
      }
      return (
        (currentPeriodValues[measureName] as number) -
        (lastPeriodValues[measureName] as number)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.stats-renderer {
  &--in-explorer {
    cursor: pointer;

    &:hover {
      background-color: $bg-primary;
    }
  }
}
</style>
